import { useEffect, useMemo, useRef, useState } from 'react';

interface MonthPickerProps {
  /** YYYY-MM，空字符串表示未选 */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /** 可选最小月份 YYYY-MM（含） */
  minMonth?: string;
  /** 可选最大月份 YYYY-MM（含） */
  maxMonth?: string;
  /** 校验失败样式 */
  invalid?: boolean;
  disabled?: boolean;
}

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function toMonthValue(y: number, m: number) {
  return `${y}-${pad2(m)}`;
}

function parseMonthValue(value: string): { year: number; month: number } | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})$/.exec(value);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (!Number.isFinite(year) || month < 1 || month > 12) return null;
  return { year, month };
}

function monthLabel(value: string) {
  const p = parseMonthValue(value);
  if (!p) return '';
  return `${p.year}年${p.month}月`;
}

function compareMonth(a: string, b: string) {
  return a.localeCompare(b);
}

/**
 * 月份选择器，样式对齐 DateRangePicker
 */
export default function MonthPicker({
  value,
  onChange,
  placeholder = '选择月份',
  className = '',
  minMonth,
  maxMonth,
  invalid = false,
  disabled = false,
}: MonthPickerProps) {
  const nowYear = new Date().getFullYear();
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => {
    const p = parseMonthValue(value);
    return p?.year ?? nowYear;
  });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const p = parseMonthValue(value);
    setViewYear(p?.year ?? new Date().getFullYear());
  }, [open, value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const displayText = useMemo(() => monthLabel(value), [value]);

  const isDisabledMonth = (y: number, m: number) => {
    const v = toMonthValue(y, m);
    if (minMonth && compareMonth(v, minMonth) < 0) return true;
    if (maxMonth && compareMonth(v, maxMonth) > 0) return true;
    return false;
  };

  const handlePick = (m: number) => {
    if (isDisabledMonth(viewYear, m)) return;
    onChange(toMonthValue(viewYear, m));
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-md border text-sm text-left transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
          invalid
            ? 'border-red-500 ring-1 ring-red-200 bg-white'
            : open
              ? 'border-primary-400 ring-2 ring-primary-100 bg-background-50'
              : 'border-background-200 bg-background-50 hover:border-background-300'
        }`}
      >
        <i className="ri-calendar-line text-foreground-400 shrink-0" />
        <span
          className={`flex-1 min-w-0 truncate ${
            displayText ? 'text-foreground-800' : 'text-foreground-400'
          }`}
        >
          {displayText || placeholder}
        </span>
        {value ? (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) handleClear();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                if (!disabled) handleClear();
              }
            }}
            className="w-5 h-5 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700 hover:bg-background-200 shrink-0"
            title="清除"
          >
            <i className="ri-close-circle-fill text-sm" />
          </span>
        ) : (
          <i
            className={`ri-arrow-down-s-line text-foreground-400 shrink-0 transition-transform ${
              open ? 'rotate-180' : ''
            }`}
          />
        )}
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-[80] w-[17rem] rounded-xl border border-background-200 bg-background-50 shadow-xl overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-background-100">
            <button
              type="button"
              onClick={() => setViewYear((y) => y - 1)}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors"
              title="上一年"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <div className="text-sm font-semibold text-foreground-900">{viewYear}年</div>
            <button
              type="button"
              onClick={() => setViewYear((y) => y + 1)}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors"
              title="下一年"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>

          <div className="p-3 grid grid-cols-3 gap-2">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
              const v = toMonthValue(viewYear, m);
              const selected = value === v;
              const disabledMonth = isDisabledMonth(viewYear, m);
              return (
                <button
                  key={m}
                  type="button"
                  disabled={disabledMonth}
                  onClick={() => handlePick(m)}
                  className={`h-9 rounded-lg text-sm transition-colors ${
                    selected
                      ? 'bg-primary-500 text-background-50 font-semibold'
                      : disabledMonth
                        ? 'text-foreground-300 cursor-not-allowed'
                        : 'text-foreground-800 hover:bg-background-100'
                  }`}
                >
                  {m}月
                </button>
              );
            })}
          </div>

          <div className="px-3 py-2 border-t border-background-100 flex items-center justify-end gap-1.5">
            <button
              type="button"
              onClick={handleClear}
              className="px-2.5 py-1 rounded-md text-xs text-foreground-600 hover:bg-background-100 transition-colors"
            >
              清除
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** 当前月份的下一个月 YYYY-MM（本月之后最早可选） */
export function nextMonthValue(from = new Date()) {
  const d = new Date(from.getFullYear(), from.getMonth() + 1, 1);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}

export function currentMonthValue(from = new Date()) {
  return `${from.getFullYear()}-${pad2(from.getMonth() + 1)}`;
}
